/**
 * Leave / absence types — one list for the whole dashboard.
 *
 * These are the same three types the leave module stores, with the same labels:
 * mirrors LEAVE_TYPES / LEAVE_TYPE_LABELS in api/utils/leaveService.js.
 *
 * Booking time away on the schedule, marking a missed visit as an absence, and
 * approving a leave request all describe the same thing, so they must offer the
 * same words. They used to disagree — the roster said "Holiday" and "Sick"
 * while leave said "Annual Leave" and "Sickness" — which made the same absence
 * read differently depending on which screen you opened it from, and left the
 * roster's rows untyped in the leave summaries.
 *
 * `key` is what the API stores in `leave_type`; `label` is what is stored in
 * `reason` and shown to people.
 */
export const LEAVE_TYPES = [
  {
    key: 'sick',
    label: 'Sickness',
    sub: 'May require cover',
    help: 'Unplanned absence. Overlapping visits return to Unassigned so you can arrange cover.',
    bar: 'bg-amber-400',
  },
  {
    key: 'annual',
    label: 'Annual Leave',
    sub: 'Planned absence',
    help: 'Planned absence — the carer is removed from this day’s availability.',
    bar: 'bg-sky-400',
  },
  {
    key: 'other',
    label: 'Other',
    sub: 'Add a clear note',
    help: 'Add a clear note so the reason is recorded for the team.',
    bar: 'bg-slate-500',
  },
];

/** Canonical key -> label, e.g. sick -> "Sickness". */
export const LEAVE_TYPE_LABELS = LEAVE_TYPES.reduce(
  (acc, type) => ({ ...acc, [type.key]: type.label }),
  {},
);

/**
 * Rows written before the types were renamed still say 'personal'; the API maps
 * that to 'annual' on read, so accept it here too.
 */
const LEGACY_ALIASES = { personal: 'annual', holiday: 'annual' };

/**
 * Resolve whatever an existing record carries — a canonical key ('sick'), a
 * stored label ('Annual Leave') or one of the roster's old words ('Holiday') —
 * back to one of the three types. Falls back to 'other' so an unrecognised
 * value is still editable rather than silently reset.
 */
export const resolveLeaveType = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return null;
  if (LEGACY_ALIASES[raw]) return LEGACY_ALIASES[raw];

  const byKey = LEAVE_TYPES.find((type) => type.key === raw);
  if (byKey) return byKey.key;

  const byLabel = LEAVE_TYPES.find((type) => type.label.toLowerCase() === raw);
  if (byLabel) return byLabel.key;

  return 'other';
};

/** The type definition for a key, for rendering a chosen option. */
export const leaveTypeByKey = (key) => LEAVE_TYPES.find((type) => type.key === key) || null;

/**
 * How one absence row reads, wherever it is shown.
 *
 * Deliberately the same rule as `leave_type_label` in api/utils/leaveService.js
 * (`serializeLeave`), which is what Leave & Approval renders: prefer the
 * canonical type, and fall back to the stored label only for rows that predate
 * the leave module and carry nothing else.
 *
 * The rota used to print `reason` raw, so an absence a manager entered from the
 * roster showed as "Holiday" on the rota and "Annual Leave" in Leave &
 * Approval — the same day off, worded two ways depending on which screen you
 * opened. Every rota surface goes through this instead.
 *
 * @param {{leave_type?: string|null, reason?: string|null}} row
 * @returns {string}
 */
export const unavailabilityLabel = (row) => {
  if (!row) return '';
  if (row.leave_type) {
    const key = resolveLeaveType(row.leave_type);
    return LEAVE_TYPE_LABELS[key] || row.leave_type;
  }
  return String(row.reason || '').trim() || 'Time away';
};

/** Filter options for a list view, with an "all" entry first. */
export const LEAVE_TYPE_FILTER_OPTIONS = [
  { value: '', label: 'All types' },
  ...LEAVE_TYPES.map((type) => ({ value: type.key, label: type.label })),
];
